<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import ScreenList from './ScreenList.vue';
import ScreenEditor from './ScreenEditor.vue';
import ChannelPreview from './ChannelPreview.vue';
import IssueList from './IssueList.vue';
import FlowMap from './FlowMap.vue';

const { t } = useI18n();
const store = useStore();

// Bot membership is the explicit per-inbox toggle from the inbox settings
// page — mirrors the backend should_send_button_flow? gate.
const isBotInbox = inbox => Boolean(inbox.bot_enabled);
const isStoreInbox = inbox => inbox.channel_type === 'Channel::Api';

// Per-channel meta for the preview tabs. WhatsApp/Messenger/Instagram are the
// Meta channels (native button rendering); "store" is our own web-store chat
// (Channel::Api) which renders the same buttons itself.
const CHANNEL_META = {
  whatsapp: { icon: 'i-ri-whatsapp-fill', iconClass: 'text-[#25d366]' },
  fb: { icon: 'i-ri-messenger-fill', iconClass: 'text-[#0a7cff]' },
  instagram: { icon: 'i-ri-instagram-fill', iconClass: 'text-[#dc2743]' },
  store: { icon: 'i-ri-store-2-fill', iconClass: 'text-n-brand' },
};
// Meta channel_type → preview key; anything else (Channel::Api) is the store.
const CHANNEL_KEY_BY_TYPE = {
  'Channel::Whatsapp': 'whatsapp',
  'Channel::FacebookPage': 'fb',
  'Channel::Instagram': 'instagram',
};
const channelKeyFor = inbox =>
  CHANNEL_KEY_BY_TYPE[inbox.channel_type] || 'store';

const channelLabel = key => {
  if (key === 'fb') return t('AUTO_REPLY_BOT_SETTINGS.CHANNELS.MESSENGER');
  if (key === 'instagram')
    return t('AUTO_REPLY_BOT_SETTINGS.CHANNELS.INSTAGRAM');
  if (key === 'store') return t('AUTO_REPLY_BOT_SETTINGS.CHANNELS.STORE');
  return t('AUTO_REPLY_BOT_SETTINGS.CHANNELS.WHATSAPP');
};
const MAX_TITLE = 20;
const MAX_BODY = 640;
const MAX_BUTTONS = 3;

const loaded = ref(false);
const isSaving = ref(false);
const channel = ref('whatsapp');
const mode = ref('day'); // day | night
const screens = ref([]);
// currentId = the screen being EDITED (driven by ScreenList).
const currentId = ref('greeting');
// previewPath = the journey shown in the phone preview (driven by tapping
// buttons INSIDE the preview) — decoupled from the editing selection.
const previewPath = ref(['greeting']);
const idSeq = ref(0);

const inboxesGetter = useMapGetter('inboxes/getInboxes');
const buttonInboxes = computed(() =>
  (inboxesGetter.value || []).filter(isBotInbox)
);

// The Meta preview tabs are always shown (they describe how the shared tree
// renders per channel); the store tab appears only when a Channel::Api inbox
// is actually on the bot.
const availableChannels = computed(() => {
  const keys = ['whatsapp', 'fb', 'instagram'];
  if (buttonInboxes.value.some(isStoreInbox)) keys.push('store');
  return keys.map(key => ({ key, ...CHANNEL_META[key] }));
});

// The pill showing how many inboxes a save touches, expandable to their names.
const showInboxList = ref(false);
const botInboxList = computed(() =>
  buttonInboxes.value.map(inbox => ({
    id: inbox.id,
    name: inbox.name,
    ...CHANNEL_META[channelKeyFor(inbox)],
  }))
);

const currentScreen = computed(() =>
  screens.value.find(s => s.id === currentId.value)
);
const screenById = id => screens.value.find(s => s.id === id);
const genId = () => {
  const id = `s${idSeq.value}`;
  idSeq.value += 1;
  return id;
};

// --- load / seed ---------------------------------------------------------
const buildScreens = inbox => {
  const flow = Array.isArray(inbox.button_flow) ? inbox.button_flow : [];
  const nodes = flow.map((n, i) => ({
    id: `s${i}`,
    isRoot: false,
    title: (n.title || '').trim(),
    text: n.content || '',
    offHours: n.content_off_hours || '',
    raw: Array.isArray(n.buttons) ? n.buttons : [],
  }));
  idSeq.value = nodes.length;
  const titleToId = {};
  nodes.forEach(n => {
    if (n.title) titleToId[n.title] = n.id;
  });
  const root = {
    id: 'greeting',
    isRoot: true,
    title: '',
    text: inbox.greeting_message || '',
    offHours: inbox.off_hours_prefix || '',
    raw: Array.isArray(inbox.greeting_buttons) ? inbox.greeting_buttons : [],
  };
  return [root, ...nodes].map(s => {
    const buttons = (s.raw || []).map(label => {
      const trimmed = (label || '').trim();
      return { label: trimmed, target: titleToId[trimmed] || null };
    });
    return {
      id: s.id,
      isRoot: s.isRoot,
      title: s.title,
      text: s.text,
      offHours: s.offHours,
      buttons,
    };
  });
};

const seed = () => {
  const rep = buttonInboxes.value[0];
  if (!rep) return;
  screens.value = buildScreens(rep);
  currentId.value = 'greeting';
  previewPath.value = ['greeting'];
};

onMounted(async () => {
  await store.dispatch('inboxes/get');
  seed();
  loaded.value = true;
});

// Invisible bidi marks travel with Arabic copied through an editor and would
// make an identical-LOOKING title miss its button. Written as escapes on
// purpose: the characters themselves are invisible in source.
const cleanText = value =>
  String(value || '')
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, '')
    .trim();

// Every quick-reply button of every APPROVED WhatsApp template on the bot's
// inboxes, deduped by text. Only APPROVED counts: a rejected or paused template
// is never delivered, so its buttons are not a door into the tree. The templates
// already ride on the inbox payload (channel.message_templates, synced from
// Meta), so this costs no request and needs no storage of our own.
const templateButtons = computed(() => {
  const byText = new Map();
  buttonInboxes.value.forEach(inbox => {
    (inbox.message_templates || []).forEach(tpl => {
      if (String(tpl.status || '').toUpperCase() !== 'APPROVED') return;
      (tpl.components || []).forEach(comp => {
        if (String(comp.type || '').toUpperCase() !== 'BUTTONS') return;
        (comp.buttons || []).forEach(btn => {
          if (String(btn.type || '').toUpperCase() !== 'QUICK_REPLY') return;
          const text = cleanText(btn.text);
          if (!text) return;
          if (!byText.has(text)) byText.set(text, { text, templates: [] });
          const names = byText.get(text).templates;
          if (tpl.name && !names.includes(tpl.name)) names.push(tpl.name);
        });
      });
    });
  });
  return [...byText.values()].sort((a, b) =>
    a.text.localeCompare(b.text, 'ar')
  );
});
/** The screen a template button opens, if someone built one. */
const screenForButton = text =>
  screens.value.find(s => !s.isRoot && cleanText(s.title) === cleanText(text));

// --- navigation ----------------------------------------------------------
// Select a screen for EDITING (the list is the navigator). Does not touch the
// preview journey.
const select = id => {
  if (id !== 'greeting' && !screenById(id)) return;
  currentId.value = id;
};
// Add a new blank reply screen, select it. Reset the preview journey so it can
// never point at a now-stale id.
const addScreen = (title = '') => {
  const id = genId();
  screens.value.push({
    id,
    isRoot: false,
    title,
    text: '',
    offHours: '',
    buttons: [],
  });
  previewPath.value = ['greeting'];
  select(id);
};
// One-tap fix for an orphan: add a greeting entry button that opens it (label
// locked to the screen title so runtime title-matching routes the tap). Refuse
// an untitled target — its label would be blank and get dropped on save.
const linkFromGreeting = id => {
  const root = screens.value.find(s => s.isRoot);
  const target = screenById(id);
  if (!root || !target || !target.title.trim()) return;
  if ((root.buttons || []).filter(b => b.label.trim()).length >= MAX_BUTTONS)
    return;
  root.buttons.push({ label: target.title, target: id });
};
// Preview-only journey navigation (tapping a button in the phone). Independent
// of the editing selection.
const previewNavigate = id => {
  if (!screenById(id)) return;
  const idx = previewPath.value.indexOf(id);
  previewPath.value =
    idx >= 0 ? previewPath.value.slice(0, idx + 1) : [...previewPath.value, id];
};
// Jump from an issue row to the offending screen: select it, then scroll the
// editor (where its fields are) into view — not the page top.
const jump = id => {
  select(id);
  nextTick(() => {
    document
      .getElementById('bot-screen-editor-anchor')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
};

// Build the missing reply for a template button — titled EXACTLY like it,
// because that title IS the wiring: the runtime matches the customer's tap by
// text, so typing it by hand is how the screen silently never fires. Jumps to
// the existing one when there already is a reply.
const replyToTemplateButton = text => {
  const clean = cleanText(text);
  const existing = screenForButton(clean);
  if (existing) {
    jump(existing.id);
    return;
  }
  addScreen(clean);
  nextTick(() => {
    document
      .getElementById('bot-screen-editor-anchor')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
};
// --- mutations -----------------------------------------------------------
const onField = (key, value) => {
  const s = currentScreen.value;
  if (!s) return;
  if (key === 'title') {
    s.title = value;
    // keep button labels in sync with the screen title (the runtime matches a
    // tap by the button TITLE, so a button's label must equal its target title)
    screens.value.forEach(scr =>
      (scr.buttons || []).forEach(b => {
        if (b.target === s.id) b.label = value;
      })
    );
  } else {
    s[key] = value;
  }
};
const onBtnLabel = (i, value) => {
  const b = currentScreen.value?.buttons[i];
  if (b) b.label = value;
};
const onBtnTarget = (i, targetId) => {
  const b = currentScreen.value?.buttons[i];
  if (!b) return;
  if (targetId) {
    b.target = targetId;
    b.label = screenById(targetId)?.title || b.label;
  } else {
    b.target = null;
  }
};
const onBtnNew = i => {
  const parent = currentScreen.value;
  if (!parent) return;
  const id = genId();
  const title = t('AUTO_REPLY_BOT_SETTINGS.LINK.NEW_SCREEN_NAME');
  screens.value.push({
    id,
    isRoot: false,
    title,
    text: '',
    offHours: '',
    buttons: [],
  });
  parent.buttons[i].target = id;
  parent.buttons[i].label = title;
  previewPath.value = ['greeting'];
  select(id);
};
const onBtnAdd = () => {
  const s = currentScreen.value;
  if (s && s.buttons.length < MAX_BUTTONS)
    s.buttons.push({ label: '', target: null });
};
const onBtnRemove = i => {
  currentScreen.value?.buttons.splice(i, 1);
};
const removeScreen = (id = currentId.value) => {
  if (id === 'greeting') return;
  const target = screenById(id);
  if (!target) return;
  const nonRootCount = screens.value.filter(s => !s.isRoot).length;
  const msg =
    nonRootCount <= 1
      ? t('AUTO_REPLY_BOT_SETTINGS.CONFIRM.DELETE_LAST')
      : t('AUTO_REPLY_BOT_SETTINGS.CONFIRM.DELETE_SCREEN', {
          title: target.title.trim() || '؟',
        });
  // eslint-disable-next-line no-alert
  if (!window.confirm(msg)) return;
  screens.value = screens.value.filter(s => s.id !== id);
  screens.value.forEach(s =>
    (s.buttons || []).forEach(b => {
      if (b.target === id) b.target = null;
    })
  );
  // Only bounce off the deleted screen — deleting an unrelated row from the
  // list must not yank the user out of the screen they're editing.
  if (currentId.value === id) currentId.value = 'greeting';
  if (previewPath.value.includes(id)) previewPath.value = ['greeting'];
};

// --- derived reachability / entry-point state ----------------------------
// Every screen id that some button opens. A titled reply screen NOT in this set
// is an orphan (the exact rule the list badges and warnings key off).
// A screen fires when an incoming message TEXT equals its title, so a template
// quick-reply button is a REAL second door — the one this page could not see. It
// warned that «هكتب طلبي هنا» and «اتصلوا بيا» were unreachable while both were
// live and reached every day from the missed-call template. Feeding both doors
// into the same set fixes the badge, the map and the warning at once.
// WHO opens each screen, in words — «متوصّلة» on its own is a claim the reader
// cannot check, and the two doors look identical from the badge. Keyed by screen
// id; each entry lists every door into it: the greeting, another screen, or a
// WhatsApp template.
const reachSources = computed(() => {
  const map = new Map();
  const add = (id, label) => {
    if (!id) return;
    const list = map.get(id) || [];
    if (!list.includes(label)) list.push(label);
    map.set(id, list);
  };
  screens.value.forEach(from => {
    (from.buttons || []).forEach(b => {
      if (!b.target) return;
      add(
        b.target,
        from.isRoot
          ? t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.VIA_GREETING')
          : t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.VIA_SCREEN', {
              title: from.title.trim(),
            })
      );
    });
  });
  templateButtons.value.forEach(btn => {
    const target = screenForButton(btn.text);
    if (!target) return;
    add(
      target.id,
      t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.VIA_TEMPLATE', {
        templates: btn.templates.join('، '),
      })
    );
  });
  return map;
});
const reachableIds = computed(() => new Set(reachSources.value.keys()));
const rootScreen = computed(() => screens.value.find(s => s.isRoot));
const rootButtonCount = computed(
  () => (rootScreen.value?.buttons || []).filter(b => b.label.trim()).length
);
const titledReplyScreens = computed(() =>
  screens.value.filter(s => !s.isRoot && s.title.trim())
);
// The greeting has no way in while reply screens exist → nobody can start.
const noEntryButtons = computed(
  () => rootButtonCount.value === 0 && titledReplyScreens.value.length > 0
);
const emptyGreetingWithButtons = computed(
  () =>
    rootScreen.value &&
    rootButtonCount.value > 0 &&
    !(rootScreen.value.text || '').trim()
);

// --- validation ----------------------------------------------------------
// Each issue is { text, id? } so IssueList can offer «روح للمشكلة» → jump(id).
const errors = computed(() => {
  const seen = new Map();
  const push = (text, id) => {
    if (!seen.has(text)) seen.set(text, { text, id });
  };
  const nodes = screens.value.filter(s => !s.isRoot);
  const firstUnnamed = nodes.find(s => !s.title.trim());
  if (firstUnnamed)
    push(
      t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.TITLE_REQUIRED'),
      firstUnnamed.id
    );

  const counts = {};
  nodes.forEach(s => {
    const tt = s.title.trim();
    if (tt) counts[tt] = (counts[tt] || 0) + 1;
  });
  Object.entries(counts).forEach(([title, count]) => {
    if (count > 1) {
      const dup = nodes.find(s => s.title.trim() === title);
      push(
        t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.DUPLICATE_TITLE', { title }),
        dup?.id
      );
    }
  });

  screens.value.forEach(s => {
    const label = s.isRoot
      ? t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.GREETING_ROW')
      : s.title.trim() || '؟';
    if (!s.isRoot && s.title.trim().length > MAX_TITLE)
      push(
        t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.TITLE_TOO_LONG', {
          title: label,
        }),
        s.id
      );
    if (
      (s.text || '').length > MAX_BODY ||
      (s.offHours || '').length > MAX_BODY
    )
      push(
        t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.BODY_TOO_LONG', { title: label }),
        s.id
      );
    if ((s.buttons || []).filter(b => b.label.trim()).length > MAX_BUTTONS)
      push(
        t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.TOO_MANY_BUTTONS', {
          title: label,
        }),
        s.id
      );
  });

  // a reply screen with no body produces an empty message the channels reject
  const screenTitles = new Set(nodes.map(s => s.title.trim()).filter(Boolean));
  screens.value.forEach(s => {
    const label = s.isRoot
      ? t('AUTO_REPLY_BOT_SETTINGS.SCREENS_LIST.GREETING_ROW')
      : s.title.trim() || '؟';
    if (!s.isRoot && s.title.trim() && !(s.text || '').trim())
      push(
        t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.EMPTY_CONTENT', { title: label }),
        s.id
      );
    // two buttons with the same label on one screen → WhatsApp rejects the message
    const labels = (s.buttons || []).map(b => b.label.trim()).filter(Boolean);
    if (new Set(labels).size !== labels.length)
      push(
        t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.DUPLICATE_BUTTON', {
          title: label,
        }),
        s.id
      );
    // a "hand to agent" button whose label equals a screen title is hijacked at runtime
    (s.buttons || []).forEach(b => {
      if (!b.target && b.label.trim() && screenTitles.has(b.label.trim()))
        push(
          t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.LEAF_COLLISION', {
            label: b.label.trim(),
          }),
          s.id
        );
    });
  });

  // night greeting = off-hours line + greeting; combined must fit the strictest
  // channel body limit (Messenger 640) or it gets truncated / rejected.
  if (
    rootScreen.value &&
    (rootScreen.value.offHours || '').trim() &&
    rootScreen.value.offHours.length +
      2 +
      (rootScreen.value.text || '').length >
      MAX_BODY
  )
    push(
      t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.GREETING_NIGHT_TOO_LONG'),
      'greeting'
    );

  return [...seen.values()];
});

const warnings = computed(() => {
  const seen = new Map();
  const push = (text, id) => {
    if (!seen.has(text)) seen.set(text, { text, id });
  };
  screens.value.forEach(s => {
    if (!s.isRoot && s.title.trim() && !reachableIds.value.has(s.id))
      push(
        t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.UNREACHABLE_WARNING', {
          title: s.title.trim(),
        }),
        s.id
      );
  });
  if (noEntryButtons.value)
    push(t('AUTO_REPLY_BOT_SETTINGS.VALIDATION.NO_ENTRY_BUTTONS'), 'greeting');
  return [...seen.values()];
});

const hasErrors = computed(() => errors.value.length > 0);

// --- save ----------------------------------------------------------------
const serialize = () => {
  const root = screens.value.find(s => s.isRoot);
  const nodes = screens.value.filter(s => !s.isRoot && s.title.trim());
  return {
    greeting_message: root.text,
    off_hours_prefix: root.offHours,
    greeting_buttons: (root.buttons || [])
      .map(b => b.label.trim())
      .filter(Boolean),
    button_flow: nodes.map(n => ({
      title: n.title.trim(),
      content: n.text,
      content_off_hours: n.offHours,
      buttons: (n.buttons || []).map(b => b.label.trim()).filter(Boolean),
    })),
  };
};

// Re-derive screens from the (freshly-updated) store, keeping the user on the
// screen they were editing instead of snapping back to the greeting.
const seedKeepingPosition = () => {
  const prevTitle =
    currentScreen.value && !currentScreen.value.isRoot
      ? currentScreen.value.title
      : null;
  seed();
  if (!prevTitle) return;
  const found = screens.value.find(s => !s.isRoot && s.title === prevTitle);
  if (found) {
    currentId.value = found.id;
    previewPath.value = ['greeting'];
  }
};

// Soft, cancellable confirms for the non-blocking foot-guns before a save.
// eslint-disable-next-line no-alert
const confirmDialog = msg => window.confirm(msg);
const passesSoftGuards = () => {
  // Untitled reply screens are already a blocking error (TITLE_REQUIRED) that
  // disables save, so there is no soft-guard for them here.
  if (
    noEntryButtons.value &&
    !confirmDialog(t('AUTO_REPLY_BOT_SETTINGS.CONFIRM.NO_ENTRY_BUTTONS'))
  )
    return false;
  if (
    emptyGreetingWithButtons.value &&
    !confirmDialog(t('AUTO_REPLY_BOT_SETTINGS.CONFIRM.EMPTY_GREETING_TEXT'))
  )
    return false;
  return true;
};

const save = async () => {
  if (hasErrors.value) {
    useAlert(t('AUTO_REPLY_BOT_SETTINGS.FIX_ERRORS'));
    return;
  }
  if (!passesSoftGuards()) return;
  isSaving.value = true;
  const payload = serialize();
  const inboxes = buttonInboxes.value;
  try {
    // updateInbox commits each server response into the store, so the store is
    // authoritative right after this. We deliberately do NOT re-fetch via the
    // cached inboxes/get afterwards: that path can serve a stale cached copy and
    // make a successful save look like it reverted (the "saved on the 2nd try" bug).
    const results = await Promise.allSettled(
      inboxes.map(inbox =>
        store.dispatch('inboxes/updateInbox', {
          id: inbox.id,
          formData: false,
          ...payload,
        })
      )
    );
    seedKeepingPosition();
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed === 0) {
      useAlert(t('AUTO_REPLY_BOT_SETTINGS.SAVE_SUCCESS'));
    } else if (failed < inboxes.length) {
      useAlert(
        t('AUTO_REPLY_BOT_SETTINGS.SAVE_PARTIAL', {
          saved: inboxes.length - failed,
          total: inboxes.length,
          failed,
        })
      );
    } else {
      useAlert(t('AUTO_REPLY_BOT_SETTINGS.SAVE_FAILED'));
    }
  } catch (error) {
    useAlert(t('AUTO_REPLY_BOT_SETTINGS.SAVE_FAILED'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="!loaded"
    :loading-message="t('AUTO_REPLY_BOT_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('AUTO_REPLY_BOT_SETTINGS.TITLE')"
        :description="t('AUTO_REPLY_BOT_SETTINGS.DESCRIPTION')"
        icon-name="chat-multiple"
      />
    </template>

    <template #body>
      <p
        v-if="!buttonInboxes.length"
        class="flex items-center justify-center py-20 text-n-slate-11"
      >
        {{ t('AUTO_REPLY_BOT_SETTINGS.NO_CHANNELS') }}
      </p>

      <div v-else class="flex flex-col gap-4">
        <!-- scope banner: these edits apply to every channel -->
        <div class="rounded-xl border border-n-brand/40 bg-n-brand/5 p-4">
          <button
            type="button"
            class="flex w-full items-start gap-2.5 text-right"
            @click="showInboxList = !showInboxList"
          >
            <span class="i-lucide-info mt-0.5 size-4 shrink-0 text-n-brand" />
            <span class="flex-1 text-[12.5px] font-bold text-n-slate-12">
              {{
                t('AUTO_REPLY_BOT_SETTINGS.SCOPE_BANNER', {
                  count: buttonInboxes.length,
                })
              }}
            </span>
            <span
              class="flex shrink-0 items-center gap-1 text-[11.5px] font-bold text-n-brand"
            >
              {{ t('AUTO_REPLY_BOT_SETTINGS.SCOPE_SHOW_CHANNELS') }}
              <span
                class="i-lucide-chevron-down size-3.5 transition-transform"
                :class="{ 'rotate-180': showInboxList }"
              />
            </span>
          </button>
          <div
            v-if="showInboxList"
            class="mt-3 flex flex-wrap gap-2 border-t border-n-brand/20 pt-3"
          >
            <span
              v-for="item in botInboxList"
              :key="item.id"
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-[12px] font-semibold text-n-slate-12"
            >
              <span :class="[item.icon, item.iconClass]" class="size-4" />
              {{ item.name }}
            </span>
          </div>
        </div>

        <!-- toolbar: save only (day/night lives on the preview) -->
        <div class="flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            :disabled="isSaving || hasErrors"
            class="inline-flex h-11 items-center gap-2 rounded-xl bg-n-brand px-5 text-[13px] font-bold text-white shadow-md transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
            @click="save"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-circle size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            <span class="flex flex-col items-start leading-tight">
              <span>{{
                isSaving
                  ? t('AUTO_REPLY_BOT_SETTINGS.SAVING')
                  : t('AUTO_REPLY_BOT_SETTINGS.SAVE')
              }}</span>
              <span class="text-[10px] font-medium opacity-80">{{
                t('AUTO_REPLY_BOT_SETTINGS.SAVE_SUBTITLE', {
                  count: buttonInboxes.length,
                })
              }}</span>
            </span>
          </button>
        </div>

        <!-- issues -->
        <IssueList :errors="errors" :warnings="warnings" @jump="jump" />

        <!-- list + editor (left) | live preview (right) -->
        <div
          class="grid grid-cols-1 items-start gap-5 min-[980px]:grid-cols-[minmax(0,1fr)_360px]"
        >
          <div class="flex flex-col gap-4">
            <ScreenList
              :screens="screens"
              :current-id="currentId"
              :reachable-ids="reachableIds"
              :reach-sources="reachSources"
              :root-button-count="rootButtonCount"
              :no-entry-buttons="noEntryButtons"
              @select="select"
              @add-screen="addScreen"
              @remove-screen="removeScreen"
              @link-from-greeting="linkFromGreeting"
            />
            <ScreenEditor
              v-if="currentScreen"
              id="bot-screen-editor-anchor"
              :screen="currentScreen"
              :screens="screens"
              @field="onField"
              @btn-label="onBtnLabel"
              @btn-target="onBtnTarget"
              @btn-new="onBtnNew"
              @btn-add="onBtnAdd"
              @btn-remove="onBtnRemove"
              @remove-screen="removeScreen"
            />
          </div>

          <div class="min-[980px]:sticky min-[980px]:top-4">
            <div class="mb-3 flex items-center justify-between px-1">
              <span
                class="flex items-center gap-2 text-[12.5px] font-bold text-n-slate-11"
              >
                <span class="i-lucide-smartphone size-4" />
                {{ t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.TITLE') }}
              </span>
              <div
                class="inline-flex rounded-lg border border-n-weak bg-n-solid-1 p-0.5"
              >
                <button
                  type="button"
                  class="flex items-center gap-1 rounded-[7px] px-2.5 py-1 text-[11.5px] font-bold transition-all"
                  :class="
                    mode === 'day'
                      ? 'bg-n-slate-12 text-n-solid-1'
                      : 'text-n-slate-10'
                  "
                  @click="mode = 'day'"
                >
                  <span class="i-lucide-sun size-3.5" />
                  {{ t('AUTO_REPLY_BOT_SETTINGS.DAY') }}
                </button>
                <button
                  type="button"
                  class="flex items-center gap-1 rounded-[7px] px-2.5 py-1 text-[11.5px] font-bold transition-all"
                  :class="
                    mode === 'night'
                      ? 'bg-n-slate-12 text-n-solid-1'
                      : 'text-n-slate-10'
                  "
                  @click="mode = 'night'"
                >
                  <span class="i-lucide-moon size-3.5" />
                  {{ t('AUTO_REPLY_BOT_SETTINGS.NIGHT') }}
                </button>
              </div>
            </div>

            <!-- channel tabs = a skin viewer, not a bot selector -->
            <div class="mb-3 flex flex-wrap gap-1.5">
              <button
                v-for="ch in availableChannels"
                :key="ch.key"
                type="button"
                class="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11.5px] font-bold transition-all"
                :class="
                  channel === ch.key
                    ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                    : 'border-n-weak bg-n-solid-1 text-n-slate-10 hover:border-n-strong'
                "
                @click="channel = ch.key"
              >
                <span :class="[ch.icon, ch.iconClass]" class="size-4" />
                {{ channelLabel(ch.key) }}
              </button>
            </div>
            <p
              class="mb-3 text-[11px] font-medium leading-relaxed text-n-slate-10"
            >
              {{ t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.TABS_ARE_SKIN') }}
            </p>

            <ChannelPreview
              :screens="screens"
              :path="previewPath"
              :channel="channel"
              :mode="mode"
              :no-entry-buttons="noEntryButtons"
              @navigate="previewNavigate"
            />
            <p
              class="mt-3 text-center text-[11.5px] font-medium leading-relaxed text-n-slate-10"
            >
              {{ t('AUTO_REPLY_BOT_SETTINGS.PREVIEW.HINT') }}
            </p>
          </div>
        </div>

        <!-- template quick-reply buttons: the OTHER door into this tree -->
        <div
          v-if="templateButtons.length"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <div class="mb-3 flex flex-wrap items-start justify-between gap-2">
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('AUTO_REPLY_BOT_SETTINGS.TEMPLATES.TITLE') }}
              </h3>
              <p class="mt-0.5 text-xs leading-relaxed text-n-slate-11">
                {{ t('AUTO_REPLY_BOT_SETTINGS.TEMPLATES.SUB') }}
              </p>
            </div>
            <span
              class="shrink-0 rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-11"
            >
              {{
                t('AUTO_REPLY_BOT_SETTINGS.TEMPLATES.COUNT', {
                  done: templateButtons.filter(b => screenForButton(b.text))
                    .length,
                  total: templateButtons.length,
                })
              }}
            </span>
          </div>
          <ul class="flex flex-col gap-1.5">
            <li
              v-for="btn in templateButtons"
              :key="btn.text"
              class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
            >
              <span class="flex min-w-0 flex-col">
                <span class="flex items-center gap-2">
                  <span class="truncate text-sm font-medium text-n-slate-12">
                    {{ btn.text }}
                  </span>
                  <span
                    class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="
                      screenForButton(btn.text)
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-amber-3 text-n-amber-11'
                    "
                  >
                    {{
                      screenForButton(btn.text)
                        ? t('AUTO_REPLY_BOT_SETTINGS.TEMPLATES.HAS_SCREEN')
                        : t('AUTO_REPLY_BOT_SETTINGS.TEMPLATES.NO_SCREEN')
                    }}
                  </span>
                </span>
                <span class="truncate text-xs text-n-slate-10">
                  {{ btn.templates.join('، ') }}
                </span>
              </span>
              <woot-button
                variant="clear"
                size="small"
                @click="replyToTemplateButton(btn.text)"
              >
                {{
                  screenForButton(btn.text)
                    ? t('AUTO_REPLY_BOT_SETTINGS.TEMPLATES.OPEN')
                    : t('AUTO_REPLY_BOT_SETTINGS.TEMPLATES.CREATE')
                }}
              </woot-button>
            </li>
          </ul>
        </div>

        <!-- flow map (collapsed overview) -->
        <FlowMap :screens="screens" :current-id="currentId" @select="select" />
      </div>
    </template>
  </SettingsLayout>
</template>
